import { useEffect, useRef, useState } from 'react';
import useAuth from './useAuth';
import useDeepVinApi from './useDeepvinApi';
import {SignOut} from '../Signin';

export function useIdleLogout(timeout = 10 * 60 * 1000, graceMs = 60 * 1000) {
  const idleTimerRef = useRef(null);       // main idle timer (10 min)
  const graceTimeoutRef = useRef(null);    // auto-logout at end of 1-min warning
  const tickIntervalRef = useRef(null);    // countdown tick (1s)
  const { setAuth } = useAuth();
  const api = useDeepVinApi(false);

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(graceMs / 1000);

  const clearGrace = () => {
    if (graceTimeoutRef.current) clearTimeout(graceTimeoutRef.current);
    graceTimeoutRef.current = null;
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    tickIntervalRef.current = null;
  };

  const resetIdle = () => {
    // Cancel grace period (if any) and hide modal
    //setShowWarning(false);
    clearGrace();

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(handleIdle, timeout);
  };

  const staySignedIn = () => {
    // console.log("staySignedIn");
    // Explicit button in modal
    setShowWarning(false);
    resetIdle();
  };

  const signOutNow = () => {
    // Explicit button in modal
    clearAll();
    SignOut(api, setAuth);
  };

  const clearAll = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
    clearGrace();
    setShowWarning(false);
  };

  const startGraceCountdown = () => {
    setSecondsLeft(graceMs / 1000);
    // Tick every second
    tickIntervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        const nxt = prev - 1;
        return nxt >= 0 ? nxt : 0;
      });
    }, 1000);

    // Auto-logout when grace is over
    graceTimeoutRef.current = setTimeout(() => {
      setShowWarning(false);
      clearGrace();
      SignOut(api, setAuth);
    }, graceMs);
  };

  const handleIdle = () => {
    // Another tab may have already logged out the user — the app-level auth
    // will typically handle that; we still proceed with warning here.
    clearGrace();
    setShowWarning(true);
    startGraceCountdown();
  };

  useEffect(() => {
    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'visibilitychange',
    ];

    // If user is active during warning, treat it like "Stay signed in"
    const onActivity = () => {
      // console.log("onActivity, showWarning: ", showWarning);
      if (!graceTimeoutRef.current) {
        // staySignedIn();
      //} else {
        // console.log("activity detected, resetting idle timer");
        resetIdle();
      }
    };

    activityEvents.forEach(ev => window.addEventListener(ev, onActivity));

    // start timer immediately
    resetIdle();

    // Listen for cross-tab logout broadcasts
    const handleLocalStorageEvent = (e) => {
      if (e.key === 'logout') {
        clearAll(); // stop timers and hide modal on this tab too
      }
    };
    window.addEventListener('storage', handleLocalStorageEvent);

    return () => {
      activityEvents.forEach(ev => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', handleLocalStorageEvent);
      clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeout, graceMs]);

  return {
    showWarning,
    secondsLeft,
    staySignedIn,
    signOutNow,
  };
}

export function IdleWarning({ open, secondsLeft, onStay, onSignOut }) {
  
  // console.log("IdleWarning open: ", open, secondsLeft);
  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'rgba(0,0,0,0.4)', zIndex: 9999
    }}>
      <div style={{
        width: 'min(92vw, 420px)', background: '#111', color: '#fff',
        borderRadius: '12px', padding: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Automatic sign-out?</h3>
        <p style={{ lineHeight: 1.5, marginBottom: '16px' }}>
          Due to inactivity, this session will be signed out in{' '}
          <strong>{secondsLeft}</strong> second{secondsLeft === 1 ? '' : 's'} unless you stay signed in.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onSignOut} style={{ padding: '8px 12px' }}>
            Sign out now
          </button>
          <button onClick={onStay} style={{ padding: '8px 12px' }}>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
